import React from 'react'
import { Link, scroller } from 'react-scroll'
import cx from 'classnames'

import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { useViewport } from '@cinch-labs/shared-util'

import { SearchButton } from '../search-button'

import { reviewSectionsData } from '../review-sections/review-sections.data'

import styles from './sticky-nav.module.css'
import sharedStyles from '../../car-reviews-v2.module.css'

interface StickyNavProps {
  make: string
  model: string
  windowBottomScrollPosition: number
  areVehiclesInStock?: boolean
}

interface NavRefs {
  [key: string]: React.RefObject<HTMLLIElement>
}

const navRefs: NavRefs = reviewSectionsData.reduce((refs, item) => {
  refs[item.id] = React.createRef<HTMLLIElement>()
  return refs
}, {} as NavRefs)

const scrollIntoView = (element: HTMLElement) => {
  const navElement = element
  const navContainer = document.querySelector(
    '.stickyNavListScrollIntoView',
  ) as HTMLElement

  if (navContainer) {
    const leftPosition =
      navElement.getBoundingClientRect().left + navContainer.scrollLeft
    const containerCenter = navContainer.clientWidth / 2
    const elementCenter = navElement.clientWidth / 2

    // Calculate the scroll position for the center alignment
    const scrollPosition = leftPosition - containerCenter + elementCenter

    // Smoothly scroll the container to the calculated position
    navContainer.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    })
  }
}
export const StickyNav = ({
  make,
  model,
  windowBottomScrollPosition,
  areVehiclesInStock,
}: StickyNavProps) => {
  const { trackEvent } = useAnalytics()

  const handleClick = (title: string) => {
    trackEvent({
      eventName: `Car Review - Review Navigation Menu - ${title}`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'review mega nav',
          label: title,
          action: make,
          value: model,
        },
      },
    })
  }

  const { vw } = useViewport({
    updateOnResize: true,
    updateOnOrientationChange: true,
  })

  const isDesktop = vw >= 1024

  const verdictSection = scroller.get('verdict')
  const verdictSectionTopPosition = verdictSection?.offsetTop // optional chain required so tests pass
  const showStickyWindowCtaButton =
    !isDesktop && windowBottomScrollPosition <= verdictSectionTopPosition

  return (
    <>
      <nav className={styles.stickyNav} aria-hidden={true}>
        <div className={cx(styles.stickyNavInner, sharedStyles.inner)}>
          <ul
            className={cx(styles.stickyNavList, 'stickyNavListScrollIntoView')}
          >
            {reviewSectionsData.map((item) => (
              <li key={item.id} aria-label={item.title} ref={navRefs[item.id]}>
                <Link
                  className={styles.stickyNavLink}
                  activeClass={styles.stickyNavLinkActive}
                  to={item.id}
                  spy={true}
                  smooth={true}
                  offset={-100}
                  data-text={item.title}
                  onClick={() => handleClick(item.id)}
                  onSetActive={() => {
                    const currentElement = navRefs[item.id].current

                    if (currentElement instanceof HTMLElement) {
                      scrollIntoView(currentElement)
                    }
                  }}
                >
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>

          {isDesktop && areVehiclesInStock && (
            <SearchButton
              make={make}
              model={model}
              small
              onClick={() => handleClick('search for vehicle')}
            />
          )}
        </div>
      </nav>

      {showStickyWindowCtaButton && areVehiclesInStock && (
        <div className={styles.stickyNavCtaMobileContainer}>
          <SearchButton
            make={make}
            model={model}
            onClick={() => handleClick('search for vehicle')}
            small
          />
        </div>
      )}
    </>
  )
}
